/**
 * Gössl Theme - Mega Menu v2
 * Handles desktop mega menu hover interactions and mobile menu (left slide)
 */

(function() {
  'use strict';

  const CLOSE_DELAY = 100;
  const SELECTORS = {
    header: '[data-header]',
    headerNav: '[data-header-nav]',
    megaMenuItem: '[data-mega-menu-item]',
    megaMenuTrigger: '[data-mega-menu-trigger]',
    megaMenuDropdownWrapper: '[data-mega-menu-dropdown-wrapper]',
    megaMenuDropdown: '[data-mega-menu-dropdown]',
    mobileMenuToggle: '[data-mobile-menu-toggle]',
    mobileMenu: '[data-mobile-menu]',
    mobileMenuClose: '[data-mobile-menu-close]',
    mobileSubmenuTrigger: '[data-mobile-submenu-trigger]',
    mobileSubmenu: '[data-mobile-submenu]'
  };

  class MegaMenu {
    constructor() {
      this.header = document.querySelector(SELECTORS.header);
      this.headerNav = document.querySelector(SELECTORS.headerNav);
      if (!this.header) return;

      // Desktop mega menu elements live inside header-nav-wrapper
      const navScope = this.headerNav || this.header;
      this.menuItems = navScope.querySelectorAll(SELECTORS.megaMenuItem);
      this.dropdownWrapper = navScope.querySelector(SELECTORS.megaMenuDropdownWrapper);

      // Mobile menu toggle can be in the nav bar
      this.mobileMenuToggle = document.querySelector(SELECTORS.mobileMenuToggle);
      this.mobileMenu = document.querySelector(SELECTORS.mobileMenu);

      this.activeMenu = null;
      this.closeTimer = null;
      this.isMobileMenuOpen = false;

      this.init();
    }

    init() {
      this.bindDesktopEvents();
      this.bindMobileEvents();
      this.bindKeyboardEvents();
      this.randomizeFeaturedProducts();
    }

    // Desktop Mega Menu
    bindDesktopEvents() {
      this.menuItems.forEach(item => {
        const trigger = item.querySelector(SELECTORS.megaMenuTrigger);
        if (!trigger) return;

        const menuId = trigger.dataset.megaMenuTrigger;
        const dropdown = (this.headerNav || this.header).querySelector(`[data-mega-menu-dropdown="${menuId}"]`);

        item.addEventListener('mouseenter', () => {
          this.clearCloseTimer();
          this.openMenu(menuId, trigger, dropdown);
        });

        item.addEventListener('mouseleave', () => {
          this.startCloseTimer();
        });

        trigger.addEventListener('click', (e) => {
          if (this.activeMenu === menuId) return;
          if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
            e.preventDefault();
            this.openMenu(menuId, trigger, dropdown);
          }
        });
      });

      if (this.dropdownWrapper) {
        this.dropdownWrapper.addEventListener('mouseenter', () => {
          this.clearCloseTimer();
        });
        this.dropdownWrapper.addEventListener('mouseleave', () => {
          this.startCloseTimer();
        });
      }

      document.addEventListener('click', (e) => {
        if (this.activeMenu && !this.header.contains(e.target) && !(this.headerNav && this.headerNav.contains(e.target))) {
          this.closeMenu();
        }
      });
    }

    openMenu(menuId, trigger, dropdown) {
      if (this.activeMenu && this.activeMenu !== menuId) {
        this.closeMenu(false);
      }
      if (!dropdown) return;

      this.activeMenu = menuId;

      const navScope = this.headerNav || this.header;
      const allTriggers = navScope.querySelectorAll(SELECTORS.megaMenuTrigger);
      allTriggers.forEach(t => {
        t.setAttribute('aria-expanded', 'false');
        t.closest(SELECTORS.megaMenuItem)?.classList.remove('is-active');
      });

      trigger.setAttribute('aria-expanded', 'true');
      trigger.closest(SELECTORS.megaMenuItem)?.classList.add('is-active');

      const allDropdowns = navScope.querySelectorAll(SELECTORS.megaMenuDropdown);
      allDropdowns.forEach(d => {
        d.hidden = true;
        d.classList.remove('is-open');
      });

      dropdown.hidden = false;
      dropdown.offsetHeight;
      dropdown.classList.add('is-open');

      this.dropdownWrapper?.classList.add('is-active');
    }

    closeMenu(animate = true) {
      if (!this.activeMenu) return;

      const navScope = this.headerNav || this.header;
      const dropdown = navScope.querySelector(`[data-mega-menu-dropdown="${this.activeMenu}"]`);
      const trigger = navScope.querySelector(`[data-mega-menu-trigger="${this.activeMenu}"]`);

      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.closest(SELECTORS.megaMenuItem)?.classList.remove('is-active');
      }

      if (dropdown) {
        dropdown.classList.remove('is-open');
        if (animate) {
          setTimeout(() => { dropdown.hidden = true; }, 200);
        } else {
          dropdown.hidden = true;
        }
      }

      this.dropdownWrapper?.classList.remove('is-active');
      this.activeMenu = null;
    }

    startCloseTimer() {
      this.closeTimer = setTimeout(() => { this.closeMenu(); }, CLOSE_DELAY);
    }

    clearCloseTimer() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    }

    // Mobile Menu (slides from left)
    bindMobileEvents() {
      if (!this.mobileMenuToggle || !this.mobileMenu) return;

      this.mobileMenuToggle.addEventListener('click', () => {
        this.toggleMobileMenu();
      });

      // Close buttons (both X and backdrop)
      const closeBtns = this.mobileMenu.querySelectorAll(SELECTORS.mobileMenuClose);
      closeBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          this.closeMobileMenu();
        });
      });

      // Submenu toggles
      const submenuTriggers = this.mobileMenu.querySelectorAll(SELECTORS.mobileSubmenuTrigger);
      submenuTriggers.forEach(trigger => {
        trigger.addEventListener('click', () => {
          this.toggleMobileSubmenu(trigger);
        });
      });
    }

    toggleMobileMenu() {
      if (this.isMobileMenuOpen) {
        this.closeMobileMenu();
      } else {
        this.openMobileMenu();
      }
    }

    openMobileMenu() {
      this.isMobileMenuOpen = true;
      this.mobileMenuToggle.setAttribute('aria-expanded', 'true');
      this.mobileMenu.hidden = false;
      this.mobileMenu.offsetHeight;
      this.mobileMenu.classList.add('is-open');
      document.body.classList.add('mobile-menu-open');
      this.mobileMenu.querySelector('.mobile-menu-overlay__panel a, .mobile-menu-overlay__panel button')?.focus();
    }

    closeMobileMenu() {
      this.isMobileMenuOpen = false;
      this.mobileMenuToggle.setAttribute('aria-expanded', 'false');
      this.mobileMenu.classList.remove('is-open');
      document.body.classList.remove('mobile-menu-open');
      const onEnd = () => {
        this.mobileMenu.hidden = true;
        this.mobileMenu.removeEventListener('transitionend', onEnd);
      };
      this.mobileMenu.addEventListener('transitionend', onEnd);
      setTimeout(() => { this.mobileMenu.hidden = true; }, 400);
      this.mobileMenuToggle.focus();
    }

    toggleMobileSubmenu(trigger) {
      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
      const submenu = trigger.nextElementSibling;
      trigger.setAttribute('aria-expanded', !isExpanded);
      if (submenu) submenu.hidden = isExpanded;
      const chevron = trigger.querySelector('.mobile-menu__chevron');
      if (chevron) chevron.classList.toggle('is-rotated', !isExpanded);
    }

    // Featured Products
    randomizeFeaturedProducts() {
      const panels = document.querySelectorAll('[data-mega-menu-featured]');
      panels.forEach(panel => {
        const products = panel.querySelectorAll('[data-mega-menu-product]');
        if (products.length === 0) return;
        const randomIndex = Math.floor(Math.random() * products.length);
        products.forEach((p, i) => { p.hidden = i !== randomIndex; });
      });
    }

    // Keyboard Navigation
    bindKeyboardEvents() {
      document.addEventListener('keydown', (e) => {
        // Escape closes any open menu
        if (e.key === 'Escape') {
          if (this.isMobileMenuOpen) {
            this.closeMobileMenu();
            return;
          }
          if (this.activeMenu) {
            const navScope = this.headerNav || this.header;
            const trigger = navScope.querySelector(`[data-mega-menu-trigger="${this.activeMenu}"]`);
            this.closeMenu();
            // Return focus to the trigger that opened the menu
            trigger?.focus();
            return;
          }
        }

        // ArrowDown / Enter / Space on a trigger opens menu and moves focus into it
        const isTrigger = e.target.hasAttribute?.('data-mega-menu-trigger');
        if (isTrigger && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          const menuId = e.target.dataset.megaMenuTrigger;
          const navScope = this.headerNav || this.header;
          const dropdown = navScope.querySelector(`[data-mega-menu-dropdown="${menuId}"]`);
          if (dropdown) {
            this.openMenu(menuId, e.target, dropdown);
            // Move focus into dropdown on ArrowDown/Enter only (not on hover opens)
            requestAnimationFrame(() => {
              dropdown.querySelector('a, button')?.focus();
            });
          }
          return;
        }

        // Tab out of menu closes it
        if (e.key === 'Tab' && this.activeMenu) {
          const navScope = this.headerNav || this.header;
          const dropdown = navScope.querySelector(`[data-mega-menu-dropdown="${this.activeMenu}"]`);
          if (dropdown && !dropdown.contains(e.target)) {
            // Give time for focus to actually move before checking
            requestAnimationFrame(() => {
              if (this.activeMenu && dropdown && !dropdown.contains(document.activeElement)) {
                const trigger = navScope.querySelector(`[data-mega-menu-trigger="${this.activeMenu}"]`);
                if (document.activeElement !== trigger) {
                  this.closeMenu();
                }
              }
            });
          }
        }
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new MegaMenu());
  } else {
    new MegaMenu();
  }
})();
